import { Component, model, input, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MultiSelect } from 'primeng/multiselect';
import { LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { UserService, UserModel } from '../../../../services/user.service';
import { ValidationService } from '../../../../services/validation.service';

@Component({
  selector: 'app-users-control',
  standalone: true,
  imports: [CommonModule, FormsModule, MultiSelect],
  template: `
    <div class="checklist-control-row">
      <label [for]="'users-' + lineItem().lineItemId" class="checklist-control-label">
        {{ lineItem().name }}@if (lineItem().isRequired) { <span class="required-indicator">*</span>}
      </label>
      <div class="checklist-control-input">
        <p-multiselect
          [inputId]="'users-' + lineItem().lineItemId"
          [(ngModel)]="selectedUserIds"
          (onChange)="onSelectionChange()"
          (onBlur)="onBlur()"
          [options]="userOptions()"
          optionLabel="displayName"
          optionValue="userId"
          [disabled]="disabled() || loading()"
          [invalid]="hasError()"
          [placeholder]="loading() ? 'Loading users...' : 'Select users...'"
          display="chip"
          [filter]="true"
          filterBy="displayName,email"
          [appendTo]="'body'"
          [scrollHeight]="'300px'"
          styleClass="w-full"
        />
        @if (hasError()) {
          @for (err of errors(); track $index) {
            <small class="p-error d-block">{{ err }}</small>
          }
        }
      </div>
      <div class="ctrl-speaker-col">
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>
    </div>
  `,
  styles: []
})
export class UsersControlComponent implements OnInit {
  lineItem = input.required<LineItemModel>();
  value = model<number[]>([]);
  disabled = input<boolean>(false);
  projectId = input<number>(0);
  showError = input<boolean>(false);

  loading = signal<boolean>(false);
  userOptions = signal<{ userId: number; displayName: string; email: string }[]>([]);
  touched = signal(false);

  // Two-way binding for the multiselect
  selectedUserIds: number[] = [];

  private speechService = inject(SpeechService);
  private userService = inject(UserService);
  private validationService = inject(ValidationService);

  errors = computed(() => {
    if (!this.showError() && !this.touched()) return [];
    return this.validationService.validate(this.lineItem(), this.selectedUserIds);
  });

  hasError = computed(() => this.errors().length > 0);

  ngOnInit(): void {
    this.loadUsers();

    // Initialize selectedUserIds from value
    const currentValue = this.value();
    if (currentValue && Array.isArray(currentValue)) {
      this.selectedUserIds = currentValue;
    }
  }

  ngOnChanges(): void {
    // Sync selectedUserIds when value changes from parent
    const currentValue = this.value();
    if (currentValue && Array.isArray(currentValue)) {
      this.selectedUserIds = currentValue;
    }
  }

  // Watch for changes in selectedUserIds and update model
  ngDoCheck(): void {
    if (JSON.stringify(this.selectedUserIds) !== JSON.stringify(this.value())) {
      this.value.set(this.selectedUserIds);
    }
  }

  onSelectionChange(): void {
    this.touched.set(true);
  }

  onBlur(): void {
    this.touched.set(true);
  }

  private loadUsers(): void {
    this.loading.set(true);
    const projectId = this.projectId();
    const usersRequest = projectId > 0
      ? this.userService.getProjectUserList(projectId)
      : this.userService.getUserList();

    usersRequest.subscribe({
      next: (users: UserModel[]) => {
        // Transform users to dropdown options (project users in project context, global users otherwise)
        const options = users
          .filter(user => user.isActive)
          .map(user => ({
            userId: user.userId!,
            displayName: `${user.firstName} ${user.lastName}`,
            email: user.email
          }));
        this.userOptions.set(options);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading users for users control:', error);
        this.loading.set(false);
        this.userOptions.set([]);
      }
    });
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
